// SystemSettings.jsx - Reusable component for system settings
import { useState } from "react";
import {
  CheckIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/16/solid";
import { IconLoading } from "../../assets/icons";
import { BOT_STRATEGIES } from "../../context/botContext";

// Radio Button Component
const RadioButton = ({ isSelected }) => (
  <div className="w-[16.5px] h-[16.5px] rounded-full border-2 border-black relative flex items-center justify-center flex-shrink-0">
    <div
      className={`w-[8px] h-[8px] bg-black rounded-full ${
        isSelected ? "scale-100" : "scale-0"
      } transition-all`}
    />
  </div>
);

// Helper function to get user-friendly parameter labels
const getParameterLabel = (key, strategy) => {
  // Capital-related fields mapping
  const capitalLabels = {
    capital: "Capital Allocation",
    amount: "Capital Allocation", // For GRID strategy
  };

  // Trade size related fields mapping
  const tradeSizeLabels = {
    amount: "Trade Size", // For XENO_ARC strategy
    trade_size: "Trade Size", // For GRID strategy
  };

  // Check if it's a capital field
  if (key === "capital") {
    return capitalLabels.capital;
  }

  // For 'amount' field, determine label based on strategy
  if (key === "amount") {
    if (strategy === BOT_STRATEGIES.GRID) {
      return capitalLabels.amount;
    } else if (
      strategy === BOT_STRATEGIES.XENO_ARC ||
      strategy === BOT_STRATEGIES.DCA
    ) {
      return tradeSizeLabels.amount;
    }
  }

  // For 'trade_size' field (GRID only)
  if (key === "trade_size") {
    return tradeSizeLabels.trade_size;
  }

  // Default: convert snake_case to Title Case
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// System Setting Card Component
const SystemSettingCard = ({ setting, isActive, onClick, strategy }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Expand automatically when selected
  useState(() => {
    if (isActive) setIsExpanded(true);
  }, [isActive]);

  // Define capital-related keys to exclude from display (but not from form data)
  const capitalKeys = ["capital", "amount"]; // 'amount' for GRID, 'capital' for others

  return (
    <div
      className={`bg-[#F2F4F8] rounded-[10px] transition border ${
        isActive
          ? "border-[#007A78] !bg-[#007A78]/10"
          : "border-transparent hover:border-gray-300"
      }`}
    >
      {/* Header */}
      <div
        className="flex justify-between gap-3 p-3 px-4 cursor-pointer items-start"
        onClick={() => {
          onClick?.();
          setIsExpanded(!isExpanded);
        }}
      >
        <div className="flex gap-3">
          <div
            className={`w-[18px] h-[18px] rounded-full border-2 flex-shrink-0 mt-1 relative ${
              isActive ? "border-[#007A78]" : "border-black"
            }`}
          >
            {isActive && (
              <div className="absolute inset-0 bg-[#007A78] rounded-full flex justify-center items-center text-white">
                <CheckIcon className="w-[12px] h-[12px]" />
              </div>
            )}
          </div>

          <div>
            <h4 className="text-base font-bold text-gray-900">
              {setting.name}
            </h4>
            <p className="text-sm text-[#21272A] leading-[1.4] mt-1">
              {setting.description}
            </p>
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="text-gray-500 hover:text-gray-700 !bg-transparent !px-0"
        >
          {isExpanded ? (
            <ChevronUpIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Expanded Parameters */}
      {isExpanded && setting.parameters?.length > 0 && (
        <div className="bg-white border-t border-gray-200 px-4 py-3 rounded-b-[10px] text-sm">
          <h5 className="font-medium text-gray-700 mb-2">Parameters</h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {setting.parameters
              .filter((param) => {
                const key = param.parameter_key;
                const value = param.parameter_value;

                // Skip zero values
                if (value === 0 || value === "0" || value === "0.0")
                  return false;

                // For GRID: hide amount only if it represents zero or is redundant
                if (
                  strategy === BOT_STRATEGIES.GRID &&
                  key === "amount" &&
                  (value === "0" || value === 0)
                ) {
                  return false;
                }

                // For XENO_ARC/DCA: hide capital only if it's zero or redundant
                if (
                  (strategy === BOT_STRATEGIES.XENO_ARC ||
                    strategy === BOT_STRATEGIES.DCA) &&
                  key === "capital" &&
                  (value === "0" || value === 0)
                ) {
                  return false;
                }

                return true;
              })
              .map((param, idx) => (
                <div
                  key={idx}
                  className="flex justify-between bg-gray-50 p-2 rounded-md border border-gray-200"
                >
                  <span className="text-gray-600">
                    {getParameterLabel(param.parameter_key, strategy)}
                  </span>
                  <span className="font-semibold text-gray-800">
                    {param.parameter_value}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

// System Settings List Component
const SystemSettings = ({
  settings,
  activeIndex,
  onSettingClick,
  strategy,
  excludeKeys = [],
}) => {
  const groupedSettings = Object.entries(settings || {});

  return (
    <div className="grid gap-4 mt-3">
      {groupedSettings.length === 0 && (
        <div className="text-gray-500 text-center text-sm py-3">
          No system profiles available.
        </div>
      )}

      {groupedSettings.map(([level, profiles]) => {
        const safeProfiles = Array.isArray(profiles)
          ? profiles
          : profiles
          ? [profiles]
          : [];

        if (safeProfiles.length === 0) return null;

        return (
          <div key={level}>
            <div className="flex flex-col gap-3">
              {safeProfiles.map((profile) => (
                <SystemSettingCard
                  strategy={strategy}
                  key={profile.profile_group_id}
                  setting={{
                    key: level,
                    name: profile.name + ` (${level.replace("_", " ")})`,
                    description: profile.description,
                    parameters: profile.parameters,
                    coin: profile.coin,
                    profile_group_id: profile.profile_group_id,
                  }}
                  isActive={activeIndex === level}
                  onClick={() => onSettingClick({ key: level, ...profile })}
                  excludeKeys={excludeKeys}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Loading State Component
export const LoadingState = ({ message }) => (
  <div className="min-h-[150px] grid place-items-center">
    <div className="text-center">
      <div className="flex justify-center items-center mx-auto mb-2 animate-spin">
        <IconLoading className="w-[24px] h-[24px]" />
      </div>
      {message}
    </div>
  </div>
);

// Empty State Component
export const EmptyState = ({ onLinkClick }) => (
  <div className="min-h-[150px] grid place-items-center">
    <div className="text-center">
      We are cooking something for you. <br />
      For now, you can try to{" "}
      <span
        className="text-blue-500 cursor-pointer underline"
        onClick={onLinkClick}
      >
        customize your own
      </span>
    </div>
  </div>
);

// Settings Section Component (collapsible section)
export const SettingsSection = ({
  title,
  settingKey,
  selectedSetting,
  setSelectedSetting,
  children,
  isOneOption = false,
}) => {
  const isSelected = selectedSetting === settingKey;
  const toggleSelection = () =>
    setSelectedSetting(isSelected ? null : settingKey);

  return (
    <div className="p-3">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={toggleSelection}
      >
        {!isOneOption && (
          <div className="flex items-center gap-2 w-full">
            <RadioButton isSelected={isSelected} />
            <span>{title}</span>
          </div>
        )}
        {!isOneOption ? (
          isSelected ? (
            <ChevronUpIcon className="w-5 h-5 text-[#B7BABD]" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-[#B7BABD]" />
          )
        ) : null}
      </div>
      {isSelected && children}
    </div>
  );
};

export default SystemSettings;
